
import { useState } from 'react';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { OrganizationTable, UserOrganizationTable } from '@/types/supabase';

const CreateOrganizationForm = ({ onSuccess }: { onSuccess?: () => void }) => {
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, refreshOrganizations } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Please enter an organization name');
      return;
    }
    
    if (!user) {
      toast.error('You must be logged in to create an organization');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Create the organization - avoid using type assertions
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .insert([{ name }])
        .select('id')
        .single();
      
      if (orgError) {
        console.error('Organization creation error:', orgError);
        throw new Error(`Failed to create organization: ${orgError.message}`);
      }
      
      if (!orgData) {
        throw new Error('Failed to create organization: No data returned');
      }
      
      // Add the user as the primary founder - avoid using type assertions
      const { error: memberError } = await supabase
        .from('user_organizations')
        .insert({
          user_id: user.id,
          organization_id: orgData.id,
          role: 'primary_founder'
        });
      
      if (memberError) {
        console.error('User organization linking error:', memberError);
        throw memberError;
      }
      
      toast.success('Organization created successfully!');
      setName('');
      
      // Refresh the organizations list
      await refreshOrganizations();
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Full error details:', error);
      toast.error(error.message || 'Failed to create organization');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create New Organization</CardTitle>
        <CardDescription>
          Set up a new organization as a Primary Founder
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="orgName">Organization Name</Label>
            <Input
              id="orgName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter organization name"
              required
            />
          </div>
        </CardContent>
        <CardFooter>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Organization'}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default CreateOrganizationForm;
